import { Icon, Loading, Text } from 'components';
import React from 'react';
import { TSearchShelfCard } from 'types';
import { TEXT_STRING } from '../../constants';
import ShelfCard, { ShelfCardType } from './ShelfCard';
import { getVisibleJobShelfRows, hasVisibleJobShelfCards } from './jobShelfRows';

const SHELF_CARD_WIDTH = 'min(calc(50% - 4px), calc((100dvh - 220px) * 0.375))';
const JOB_SHELF_CARD_WIDTH =
  'min(42vw, calc((100dvh - 220px) * 0.375), 168px)';

export type ShelfCardsViewProps = {
  isLoading: boolean;
  type: ShelfCardType;
  cards: Array<TSearchShelfCard>;
  isActive: boolean;
  isTrackingReady: boolean;
  onSelectCard: (card: TSearchShelfCard) => void;
  userId?: number;
};

function ShelfCardsView({
  isLoading,
  type,
  cards,
  isActive,
  isTrackingReady,
  onSelectCard,
  userId,
}: ShelfCardsViewProps) {
  if (isLoading) {
    return (
      <div className="mt-8">
        <Loading />
      </div>
    );
  }

  if (!cards.length) {
    return (
      <Text bold fontSize={16} className="py-8px flex justify-center">
        {TEXT_STRING.COMMON.NO_DATA}
      </Text>
    );
  }

  if (type === 'job') {
    if (!hasVisibleJobShelfCards(cards)) {
      return (
        <Text bold fontSize={16} className="py-8px flex justify-center">
          {TEXT_STRING.COMMON.NO_DATA}
        </Text>
      );
    }

    const visibleRows = getVisibleJobShelfRows(cards);

    return (
      <div className="flex flex-col gap-20px bg-white py-12px">
        {visibleRows.map((row) => (
          <section
            key={row.group}
            data-testid={`job-shelf-row-${row.group}`}
            aria-label={row.title}
            className="min-w-0"
          >
            <div className="mb-10px flex items-center gap-8px px-12px">
              <Icon
                icon={row.icon}
                width={18}
                height={18}
                color="var(--color-neutral-700)"
              />
              <Text bold fontSize={14} className="shrink-0">
                {row.title}
              </Text>
              <div className="h-px flex-1 bg-neutral-200" />
            </div>
            <div
              className="flex gap-12px overflow-x-auto overscroll-x-contain px-12px pb-4px"
              data-horizontal-scroll
            >
              {row.cards.map((card) => (
                <div
                  key={`job-${card.id}`}
                  className="shrink-0"
                  style={{ width: JOB_SHELF_CARD_WIDTH }}
                >
                  <ShelfCard
                    card={card}
                    onSelect={onSelectCard}
                    type={type}
                    isActive={isActive}
                    isTrackingReady={isTrackingReady}
                    userId={userId}
                  />
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    );
  }

  return (
    <div
      className="grid grid-cols-2 justify-center gap-x-8px gap-y-12px px-8px py-12px bg-white"
      style={{ gridTemplateColumns: `repeat(2, ${SHELF_CARD_WIDTH})` }}
    >
      {cards.map((card) => (
        <ShelfCard
          key={`${type}-${card.id}`}
          card={card}
          onSelect={onSelectCard}
          type={type}
          isActive={isActive}
          isTrackingReady={isTrackingReady}
          userId={userId}
        />
      ))}
    </div>
  );
}

export default ShelfCardsView;
